import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { portalGet, portalPost } from '@/lib/portal/customerClient';
import { usePortalAuthStore } from '@/stores/portalAuth.store';

const enc = encodeURIComponent;

export interface PortalVisit {
  appointmentId: string;
  jobId: string | null;
  label: string;
  serviceName: string | null;
  status: string;
  /** Why a visit that won't happen didn't: missed (nobody home), or the plan was paused. */
  closedReason?: 'missed' | 'paused' | null;
  /** Instant, rendered in `timezone`. Null for a day-bound visit not yet timed. */
  startAt: string | null;
  endAt: string | null;
  /** Promised calendar day (date-only) for a day-bound visit without a time. */
  scheduledDate: string | null;
  timezone: string;
  arrivalWindow: { start: string; end: string } | null;
  /** A visit with no day yet: the days (date-only, inclusive) it will fall within. */
  period: { firstDay: string; lastDay: string | null } | null;
  technicianName: string | null;
  serviceAddress: {
    street?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  } | null;
}

export interface PortalVisitsResult {
  upcoming: PortalVisit[];
  past: PortalVisit[];
}

/** The signed-in contact's upcoming + past service visits. */
export function usePortalVisits() {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'visits', token],
    queryFn: () => portalGet<PortalVisitsResult>('/api/portal/visits'),
    enabled: Boolean(token),
    retry: false,
  });
}

/** An arrival window the visit can move to. */
export interface PortalRescheduleWindow {
  /** Opaque; sent back as-is to choose this window. */
  offerId: string;
  startAt: string;
  endAt: string;
}

export interface PortalAlternativesResult {
  windows: PortalRescheduleWindow[];
  /** The zone of the visit's address, which the customer reads times in. */
  timezone: string;
  /** A visit in a recurring plan moves only this many days either way. */
  recurringWindowDays: number | null;
}

/** The arrival windows an owned visit can move to (fetched on demand). */
export function usePortalVisitAlternatives(appointmentId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['portal', 'visit-alternatives', appointmentId],
    queryFn: () =>
      portalGet<PortalAlternativesResult>(`/api/portal/visits/${enc(appointmentId)}/alternatives`),
    enabled,
    retry: false,
  });
}

/** Read-only preview of the cancellation fee (null = free) before confirming. */
export function usePortalCancelPreview(appointmentId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['portal', 'visit-cancel-preview', appointmentId],
    queryFn: () =>
      portalGet<{ feeAmount: number | null }>(
        `/api/portal/visits/${enc(appointmentId)}/cancel-preview`,
      ),
    enabled,
    retry: false,
  });
}

function invalidateVisits(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['portal', 'visits'] });
  // The home hero's "next visit" is derived from the visits list, so any
  // reschedule/cancel/book must refresh it too.
  void queryClient.invalidateQueries({ queryKey: ['portal', 'home'] });
}

/** Booking (a NEW or covered visit) can also consume a membership's covered
 *  visit, so refresh the membership surfaces on top of the visit/home ones. */
function invalidateAfterBooking(queryClient: ReturnType<typeof useQueryClient>) {
  invalidateVisits(queryClient);
  void queryClient.invalidateQueries({ queryKey: ['portal', 'memberships'] });
  void queryClient.invalidateQueries({ queryKey: ['portal', 'membership'] });
}

export interface PortalRescheduleResult {
  appointmentId: string;
  /** The window the visit is now promised. */
  window: { startAt: string | null; endAt: string | null };
  timezone: string;
  status: string;
}

/** Move the visit to a chosen arrival window. */
export function usePortalRescheduleVisit(appointmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { offerId: string; reason?: string }) =>
      portalPost<PortalRescheduleResult>(
        `/api/portal/visits/${enc(appointmentId)}/reschedule`,
        body,
      ),
    onSuccess: () => invalidateVisits(queryClient),
  });
}

/** Cancel the visit (customer-initiated; a fee may be charged — see preview). */
export function usePortalCancelVisit(appointmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) =>
      portalPost<{
        status: string;
        cancellationFee: { amount: number; invoiceNumber: string } | null;
        depositRefund: { amount: number } | null;
      }>(`/api/portal/visits/${enc(appointmentId)}/cancel`, { reason }),
    onSuccess: () => invalidateVisits(queryClient),
  });
}

// ─── Book a brand-new visit ("Request a visit") ────────────────────────────

export interface PortalBookableService {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  priceCents: number;
  depositRequired: boolean;
}

export interface PortalBookingAddress {
  id: string;
  label: string | null;
  street: string;
  city: string;
  state: string;
  zip: string;
}

export interface PortalBookingSlot {
  startAt: string;
  endAt: string;
  arrivalWindow: { start: string; end: string };
}

export interface PortalBookingAvailability {
  timezone?: string;
  availability: Array<{ date: string; slots: PortalBookingSlot[] }>;
}

export interface PortalBookResult {
  referenceNumber: string;
  bookingId: string;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  arrivalWindow: { start: string; end: string } | null;
  timezone: string;
  serviceName: string;
  companyName: string;
  depositRequired: boolean;
  depositAmount: number;
  depositUrl: string | null;
  quotedTotal: number | null;
  quotedTaxTotal: number | null;
}

/** The tenant's bookable services for the "Request a visit" picker. `enabled`
 *  lets the booking modal defer the fetch until it actually opens. */
export function usePortalBookableServices(enabled = true) {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'bookable-services', token],
    queryFn: () => portalGet<PortalBookableService[]>('/api/portal/visits/book/services'),
    enabled: enabled && Boolean(token),
    retry: false,
  });
}

/** The signed-in contact's own service addresses. `enabled` lets the booking
 *  modal defer the fetch until it actually opens. */
export function usePortalBookingAddresses(enabled = true) {
  const token = usePortalAuthStore((s) => s.token);
  return useQuery({
    queryKey: ['portal', 'booking-addresses', token],
    queryFn: () => portalGet<PortalBookingAddress[]>('/api/portal/visits/book/addresses'),
    enabled: enabled && Boolean(token),
    retry: false,
  });
}

/** Open slots for a service + owned address in a given month (YYYY-MM). */
export function usePortalBookingAvailability(
  params: { serviceId: string; addressId: string; month: string },
  enabled: boolean,
) {
  const { serviceId, addressId, month } = params;
  return useQuery({
    queryKey: ['portal', 'booking-availability', serviceId, addressId, month],
    queryFn: () =>
      portalGet<PortalBookingAvailability>(
        `/api/portal/visits/book/availability?serviceId=${enc(serviceId)}&addressId=${enc(
          addressId,
        )}&month=${enc(month)}`,
      ),
    enabled: enabled && Boolean(serviceId) && Boolean(addressId) && Boolean(month),
    retry: false,
  });
}

/** Confirm a brand-new visit. */
export function usePortalBookVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { serviceId: string; addressId: string; startAt: string; notes?: string }) =>
      portalPost<PortalBookResult>('/api/portal/visits/book', body),
    onSuccess: () => invalidateAfterBooking(queryClient),
  });
}
